import { Fragment } from "react";
import { NavLink } from "react-router-dom";
import { Dialog, Transition } from "@headlessui/react";

import { XMarkIcon } from "@heroicons/react/24/outline";

import { useTranslation } from "react-i18next";

import useCustomerDetails from "../../../data/useCustomerDetails";
import classNames from "../../../utils/classNames";

export interface MobileSidebarProps {
  sidebarOpen: boolean;
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
  navigation: any[];
}

export default function MobileSidebar({
  sidebarOpen,
  setSidebarOpen,
  navigation,
}: MobileSidebarProps) {
  const { t } = useTranslation("common");
  const { brand } = useCustomerDetails();
  const brandLogo = brand.data?.data.logo;

  return (
    <Transition.Root show={sidebarOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-40 md:hidden"
        onClose={setSidebarOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75" />
        </Transition.Child>

        <div className="fixed inset-0 z-40 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative flex w-full max-w-xs flex-1 flex-col bg-primary-700 pt-5 pb-4">
              <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-300"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-300"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <div className="absolute top-0 right-0 -mr-12 pt-2">
                  <button
                    type="button"
                    className="ml-1 flex h-10 w-10 items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
                    onClick={() => setSidebarOpen(false)}
                  >
                    <span className="sr-only">{t("Close sidebar")}</span>
                    <XMarkIcon
                      className="h-6 w-6 text-white"
                      aria-hidden="true"
                    />
                  </button>
                </div>
              </Transition.Child>
              <div className="flex flex-shrink-0 items-center px-4">
                <img className="h-8 w-auto" src={brandLogo} alt="Company Logo" />
              </div>
              <div className="mt-5 h-0 flex-1 overflow-y-auto">
                <nav className="space-y-1 px-2">
                  {navigation.map((item) => (
                    item.hidden ? null : item.disabled ? (
                      <div
                        className="group flex items-center px-2 py-2 text-sm text-gray-400 font-medium rounded-md"
                        key={item.name}
                      >
                        <item.icon
                          className="mr-3 h-6 w-6 flex-shrink-0 text-currentColor"
                          aria-hidden="true"
                        />
                        {item.name}
                      </div>
                    ) : (
                      <NavLink
                        key={item.name}
                        to={item.to}
                        onClick={() => setSidebarOpen(false)}
                        className={({ isActive }) =>
                          classNames("group flex justify-between items-center px-2 py-2 text-sm font-medium rounded-md",
                            isActive ? "bg-primary-700 text-primary-50" : "text-primary-200"
                          )}
                      >
                        <div className="flex items-center">
                          <item.icon
                            className="mr-3 h-6 w-6 flex-shrink-0 text-currentColor"
                            aria-hidden="true"
                          />
                          {item.name}
                        </div>
                        {item.notificationCount && item.notificationCount > 0 && (
                          <span className="ml-auto inline-block py-0.5 px-3 text-xs font-medium bg-red-500 text-white rounded-full">
                            {item.notificationCount}
                          </span>
                        )}
                      </NavLink>
                    )
                  ))}
                </nav>
              </div>
            </Dialog.Panel>
          </Transition.Child>
          <div className="w-14 flex-shrink-0" aria-hidden="true">
            {/* Dummy element to force sidebar to shrink to fit close icon */}
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
